import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { useRouteMatch } from 'react-router';

import { userSelectors } from 'reducers';
import { userSchemas } from 'schemas';
import { useDebounce, useLocalStorageFilters, useUserRoleMatchCategory } from 'utils/hooks';
import { useGetRolesQuery, useGetUsersQuery } from 'api/main';
import {
    listingActionButtonStyle,
    ORDER_BY,
    roleCategories,
    sortDirection,
    UserEnrichments,
} from 'utils/constants';
import { texts } from 'utils/languages/fr';

import {
    CustomTablePagination,
    EmptyTableBody,
    EnhancedTableHead,
    ActionButton,
    QuickSearchField,
} from 'components';
import {
    Typography,
    TableContainer,
    Table,
    TableRow,
    TableBody,
    TableFooter,
    Box,
} from '@mui/material';
import PromoterUserRow from './PromoterUserRow';

const tableHeads = [
    { id: 'online', width: 5, isSortable: false, label: texts.ONLINE },
    { id: ORDER_BY.lastName, width: 19, isSortable: true, label: texts.NOM },
    { id: ORDER_BY.email, width: 19, isSortable: true, label: texts.ADRESSE_EMAIL },
    { id: 'phoneNumber', width: 19, isSortable: false, label: texts.PHONE_NUMBER },
    { id: ORDER_BY.role, width: 15, isSortable: true, label: texts.ROLE },
    { id: 'promoter', width: 14, isSortable: true, label: texts.PROMOTER },
    { id: ORDER_BY.login, width: 19, isSortable: true, label: texts.LAST_LOGIN_DATE },
    { id: 'actions', width: 5, isSortable: false, label: '' },
];

const getDefaultFilters = () => ({
    page: 1,
    orderBy: ORDER_BY.id,
    direction: sortDirection.DESC,
    keyword: '',
    role: [],
    enrichWith: [UserEnrichments.ROLES, UserEnrichments.SUBSCRIPTIONS, UserEnrichments.PROMOTER],
});

const PromoterUserTable = () => {
    const { url } = useRouteMatch();
    const dispatch = useDispatch();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const userIsServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);

    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);
    const [filters, setFilters] = useLocalStorageFilters(
        'promoterUserFilters',
        getDefaultFilters(),
        !!internalUser?.impersonatedByUser || !!debouncedKeyword
    );

    const filteredTableHeads = useMemo(
        () => tableHeads.filter(th => (userIsPromoter ? th.id !== 'promoter' : true)),
        [userIsPromoter]
    );

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const {
        data: { userList = [], lastPage } = { userList: [], lastPage: 1 },
        isFetching: listLoader,
    } = useGetUsersQuery(
        userSchemas.getUsers({
            ...filters,
            role: roles.filter(r => r.category?.name === roleCategories.PROMOTERS).map(r => r.id),
        }),
        { skip: !roles.length }
    );
    const dataIsLoading = useMemo(() => rolesLoader || listLoader, [rolesLoader, listLoader]);

    const handlePageChange = useCallback(
        page => () => {
            setFilters(oldValue => ({ ...oldValue, page }));
        },
        [setFilters]
    );

    const handleSortPromoterUsers = useCallback(
        (property, direction) => {
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [setFilters]
    );

    useEffect(() => {
        if (filters.page > lastPage && lastPage > 0) {
            setFilters(prevState => ({ ...prevState, page: lastPage }));
        }
    }, [lastPage, filters, setFilters]);

    useEffect(() => {
        setFilters(prevState => ({
            ...prevState,
            page: 1,
            keyword: debouncedKeyword,
            direction: debouncedKeyword ? prevState.direction : sortDirection.DESC,
            orderBy: debouncedKeyword ? prevState.orderBy : ORDER_BY.subscriptionId,
        }));
    }, [debouncedKeyword, setFilters]);

    return (
        <React.Fragment>
            <Typography variant="h5" align="left" gutterBottom>
                {userIsServicer ? texts.PROMOTER_USERS : texts.USERS}
            </Typography>
            <Box display="flex" marginY={3} justifyContent="space-between" alignItems="center">
                <QuickSearchField
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    handleClear={() => setKeyword('')}
                />
                <ActionButton
                    variant="outlined"
                    sx={listingActionButtonStyle}
                    onClick={() => dispatch(push(`${url}/invite-promoter`))}
                >
                    {texts.PROMOTER_CREATE_USER}
                </ActionButton>
            </Box>
            {!listLoader && debouncedKeyword && (
                <Typography variant="body2" gutterBottom>
                    {texts.RESULTS_FOR_KEYWORD(debouncedKeyword)}
                </Typography>
            )}
            <TableContainer sx={{ marginTop: '30px' }}>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {filteredTableHeads.map(th => (
                            <col key={th.id} width={`${th.width}%`} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={filteredTableHeads}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSortPromoterUsers}
                    />
                    {userList.length === 0 || dataIsLoading ? (
                        <EmptyTableBody
                            colSpan={filteredTableHeads.length}
                            loading={dataIsLoading}
                        />
                    ) : (
                        <TableBody>
                            {userList.map(user => (
                                <PromoterUserRow
                                    key={user.id}
                                    user={user}
                                    userIsPromoter={userIsPromoter}
                                />
                            ))}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={tableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};

export default PromoterUserTable;
